// Viste admin dei preferiti sui consulenti: nella scheda del cliente quali ha
// salvato, nella scheda del consulente da quanti clienti è stato salvato.
import { Link } from 'react-router-dom';
import { Spinner } from '@klaro7/ui';

import { useAdminProfessionalFavorites, useAdminUserFavorites } from '../hooks/useFavorites';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function CardShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white">
      <div className="border-b border-neutral-100 px-5 py-3.5">
        <h3 className="text-sm font-semibold text-neutral-700">{title}</h3>
        <p className="text-xs text-neutral-500">{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

/** Scheda cliente: i consulenti che ha tra i preferiti. */
export function AdminUserFavoritesCard({ userId }: { userId: string }) {
  const { data, isLoading, isError } = useAdminUserFavorites(userId);
  const items = data ?? [];

  return (
    <CardShell
      title="Consulenti preferiti"
      subtitle="I consulenti che il cliente ha salvato nella sua area"
    >
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : isError ? (
        <p className="px-5 py-6 text-sm text-red-600">Impossibile caricare i preferiti.</p>
      ) : items.length === 0 ? (
        <p className="px-5 py-6 text-sm text-neutral-400">Nessun consulente tra i preferiti.</p>
      ) : (
        <ul className="divide-y divide-neutral-100">
          {items.map((f) => (
            <li
              key={f.professionalId}
              className="flex items-center justify-between gap-3 px-5 py-3 text-sm"
            >
              <div className="min-w-0">
                <Link
                  to={`/dashboard/admin/professionals/${f.professionalId}`}
                  className="font-medium text-brand-teal hover:underline"
                >
                  {f.name}
                </Link>
                {f.title && <p className="truncate text-xs text-neutral-500">{f.title}</p>}
              </div>
              <span className="whitespace-nowrap text-xs text-neutral-400">
                dal {formatDate(f.createdAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </CardShell>
  );
}

/** Scheda consulente: i clienti che lo hanno salvato tra i preferiti. */
export function AdminProfessionalFavoritesCard({ professionalId }: { professionalId: string }) {
  const { data, isLoading, isError } = useAdminProfessionalFavorites(professionalId);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <CardShell
      title="Salvato tra i preferiti"
      subtitle={
        total === 0
          ? 'Nessun cliente lo ha ancora salvato'
          : `${total} ${total === 1 ? 'cliente lo ha salvato' : 'clienti lo hanno salvato'}`
      }
    >
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : isError ? (
        <p className="px-5 py-6 text-sm text-red-600">Impossibile caricare i preferiti.</p>
      ) : items.length === 0 ? (
        <p className="px-5 py-6 text-sm text-neutral-400">Nessun cliente tra i preferiti.</p>
      ) : (
        <>
          <ul className="divide-y divide-neutral-100">
            {items.map((f) => (
              <li key={f.userId} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
                <div className="min-w-0">
                  <Link
                    to={`/dashboard/admin/users/${f.userId}`}
                    className="font-medium text-brand-teal hover:underline"
                  >
                    {f.name}
                  </Link>
                  {f.email && <p className="truncate text-xs text-neutral-500">{f.email}</p>}
                </div>
                <span className="whitespace-nowrap text-xs text-neutral-400">
                  dal {formatDate(f.createdAt)}
                </span>
              </li>
            ))}
          </ul>
          {total > items.length && (
            <p className="border-t border-neutral-100 px-5 py-3 text-xs text-neutral-400">
              Mostrati i {items.length} più recenti su {total}.
            </p>
          )}
        </>
      )}
    </CardShell>
  );
}
